import {
    Badge,
    createStyles,
    Tab,
    WithStyles,
    withStyles,
} from "@material-ui/core";
import { TabProps } from "@material-ui/core/Tab";
import classnames from "classnames";
import * as React from "react";

import { MergeTypes, RelodeTheme } from "../";
import { ExclamationIcon } from "../../assets/icons";
import { baseColors } from "../styles/baseColors";

export const determineChangeIndicator = (
    change: boolean,
    error: boolean
): TabIndicator => {
    if (error) {
        return "error";
    } else if (change) {
        return "change";
    } else {
        return null;
    }
};

export const styles = (theme: RelodeTheme) =>
    createStyles({
        tab: {
            textTransform: "lowercase",
            minWidth: 80,
            textAlign: "start",
            letterSpacing: 0,
            lineHeight: "24px",
            overflow: "visible",
            [theme.breakpoints.down("xs")]: {
                fontSize: "16px",
            },
            [theme.breakpoints.down("sm")]: {
                fontSize: "16px",
            },
            [theme.breakpoints.down("md")]: {
                fontSize: "16px",
            },
            [theme.breakpoints.down("lg")]: {
                fontSize: "16px",
            },
            [theme.breakpoints.down("xl")]: {
                fontSize: "16px",
            },
        },
        badge: {
            top: "-2px",
            backgroundColor: baseColors.magenta,
        },
        changeDot: {
            top: "50%",
            right: "-8px",
            backgroundColor: baseColors.navy,
        },
        infoDot: {
            top: "50%",
            right: "-8px",
        },
        errorIcon: {
            right: "2px",
            marginLeft: theme.spacing(1),
            position: "absolute",
            width: ".75em",
        },
        error: {
            paddingRight: 10,
        },
        count: {
            top: "-1px",
            right: "-10px",
        },
        selected: {
            opacity: 1,
        },
        textColorInherit: {
            opacity: 0.8,
        },
        errorWrapper: {
            padding: theme.spacing(1, 0),
        },
    });

export type TabIndicator = "change" | "info" | "error" | null;

type TabItemPropType = MergeTypes<WithStyles<typeof styles>, TabProps>;

interface Props extends TabItemPropType {
    count?: number;
    tabIndicator?: TabIndicator;
}

export const TabItem = withStyles(styles)((props: Props) => {
    const { classes, label, count, tabIndicator, ...other } = props;

    const badgeClasses = classnames(classes.badge, {
        [classes.changeDot]: !count && tabIndicator === "change",
        [classes.infoDot]: !count && tabIndicator === "info",
        [classes.count]: count > 0,
    });

    const wrapperClasses = classnames({
        [classes.errorWrapper]: tabIndicator === "error",
    });

    const extendedLabel =
        tabIndicator === "error" ? (
            <span className={classes.error}>
                {label}{" "}
                <ExclamationIcon color="error" className={classes.errorIcon} />
            </span>
        ) : (
            label
        );

    const content =
        (count !== undefined && count > 0) ||
        tabIndicator === "change" ||
        tabIndicator === "info" ? (
            <Badge
                badgeContent={count}
                color={tabIndicator === "error" ? "error" : "primary"}
                variant={!count ? "dot" : "standard"}
                classes={{ badge: badgeClasses }}
                max={10}
            >
                {extendedLabel}
            </Badge>
        ) : (
            extendedLabel
        );

    return (
        <Tab
            {...other}
            label={content}
            className={classes.tab}
            fullWidth={false}
            classes={{
                wrapper: wrapperClasses,
                selected: classes.selected,
                textColorInherit: classes.textColorInherit,
            }}
            wrapped={false}
        />
    );
});
